/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { Preloader } from '@smartsheet/lodestar-core';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import bridgeWhiteLogo from '../../../images/bridge-white.svg';
import {
  getCookie,
  getQueryParam,
  getRedirectAfterSignInPath,
  setToken,
  initiateSmartsheetSignIn,
} from '../../../utils/signIn';
import { SignInEmailPassword, SignInMethodSelection } from '../../features/SignIn';
import { FailureModal } from '../../features/SignIn/FailureModal';
import { FormContainer, Container } from './styled';

export enum SignInStage {
  SELECTION,
  SIGN_IN_EMAIL_PASSWORD,
}

const LOGO_PADDING_BOTTOM = 40;
const LOGO_HEIGHT = 54;

const logoStyle = css`
  padding-bottom: ${LOGO_PADDING_BOTTOM}px;
`;

const logoAndFormContainerStyle = css`
  margin-top: -${LOGO_HEIGHT + LOGO_PADDING_BOTTOM}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const SignIn = () => {
  const { t: translate } = useTranslation();

  const isErrQueryParamSet = getQueryParam('error') !== null;
  const [didLoginFail] = useState(isErrQueryParamSet);
  const [isFaliureModalOpen, setIsFaliureModalOpen] =
    useState(isErrQueryParamSet);
  const [stage, setStage] = useState<SignInStage>(SignInStage.SELECTION);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoginWithBridgeEnabled, setIsLoginWithBridgeEnabled] =
    useState(false);

  useEffect(() => {
    const oneTimeQuery = getCookie('oneTimeQuery');
    if (oneTimeQuery) {
      fetch(`/v2/oauth/token?${oneTimeQuery}`)
        .then(async response => {
          if (response.ok) {
            const parsedAuthResponse = await response.json();
            setToken(JSON.stringify(parsedAuthResponse.token.token));
            window.location.href = getRedirectAfterSignInPath();
          } else {
            setIsLoading(false);
          }
        })
        .catch(() => setIsLoading(false));
    } else {
      fetch('/api/v2/providers/me/sign-in-method', {
        method: 'GET',
      })
        .then(async response => {
          response.json().then(signInMethod => {
            if (
              signInMethod.IsLoginWithBridgeEnabled === false &&
              didLoginFail === false
            ) {
              initiateSmartsheetSignIn();
            } else {
              setIsLoginWithBridgeEnabled(
                signInMethod.IsLoginWithBridgeEnabled
              );
              setIsLoading(false);
            }
          });
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  }, [didLoginFail]);

  const getComponentForState = () => {
    switch (stage) {
      case SignInStage.SELECTION:
        return (
          <SignInMethodSelection
            isLoginWithBridgeEnabled={isLoginWithBridgeEnabled}
            setStage={setStage}
          />
        );
      case SignInStage.SIGN_IN_EMAIL_PASSWORD:
        return <SignInEmailPassword setStage={setStage} />;
      default:
        return (
          <SignInMethodSelection
            isLoginWithBridgeEnabled={isLoginWithBridgeEnabled}
            setStage={setStage}
          />
        );
    }
  };

  return (
    <React.Fragment>
      <Helmet>
        <title>{translate('signin:title')}</title>
      </Helmet>
      <Container id="signin-page" isLoading={isLoading}>
        {isLoading ? (
          <Preloader />
        ) : (
          <div css={logoAndFormContainerStyle}>
            <img src={bridgeWhiteLogo} alt="bridge logo" css={logoStyle} />
            <FormContainer>
              {getComponentForState()}
              <FailureModal
                isOpen={isFaliureModalOpen}
                setIsOpen={setIsFaliureModalOpen}
              />
            </FormContainer>
          </div>
        )}
      </Container>
    </React.Fragment>
  );
};
